import React from 'react'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { RacingTitle } from '@mobi/betslip/Components/Common/BetCard/Components/EventDetails/Components/RacingTitle'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors } from '@mobi/component-library/Foundations'
import { Icon as IconLegacy } from '@mobi/component-library/Legacy/Icon'
import { toMoney } from '@mobi/utils/money'
import { StatusSilks } from '@core/Areas/Account/Pages/Activity/Components/Ticket/Components/StatusSilks'
import { TicketDisplayContext } from '@core/Areas/Account/Pages/Activity/context'
import {
  sameRaceMultiPositionToText,
  sortSameRaceMultiTicketLegs,
} from '@core/Areas/Account/Pages/Activity/helpers'
import type { Ticket, TicketStatus } from '@core/Areas/Account/Pages/Activity/types'
import { trackBetLoopNavigateToRace } from '@core/Areas/BetLoop/helpers/analytics'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import { getRaceLink } from '@core/Areas/Racing/helpers/getRaceLink'
import { CountdownStatus } from '../Common/CountdownStatus'

export const SameRaceMulti: React.FC<{
  ticket: Ticket | BetLoopShareTicket
  status: TicketStatus
}> = ({ ticket, status }) => {
  const ticketContext = React.useContext(TicketDisplayContext)

  const isContextPendingBetsDrawer = ticketContext.type === 'pending-bets-drawer'
  const isContextShare = ticketContext.type === 'share'

  const isBetLoopShare = isContextShare && ticketContext.data.display === 'bet-loop'
  const isBetLoopQuickShare = isContextShare && ticketContext.data.display === 'betloop-quick-share'

  const sortedLegs = sortSameRaceMultiTicketLegs(ticket)
  const leg = sortedLegs[0]

  const shouldProvideLink = !!(
    (ticketContext.type === 'account-activity' || isContextPendingBetsDrawer || isBetLoopShare) &&
    leg.fixtureId &&
    leg.fixtureDate &&
    leg.raceNumber
  )

  const linkInfo = shouldProvideLink
    ? {
        pathname: getRaceLink({
          meetingDate: leg.fixtureDate,
          meetingId: leg.fixtureId,
          raceNumber: leg.raceNumber,
          isFOO: false,
        }),
        handleNavigationTracking: isBetLoopShare
          ? () => trackBetLoopNavigateToRace(ticket)
          : undefined,
      }
    : undefined

  const shouldHideEventDetails = isContextPendingBetsDrawer && ticketContext.data.isSkyRaceMatch
  const shouldHideSelectionInfo = isContextShare && !isBetLoopQuickShare

  return (
    <>
      {shouldHideSelectionInfo && (
        <BetCardTitle
          title={`${ticket.legs.length} Leg Same Race Multi`}
          rightComponent={
            typeof ticket.combinedDividend === 'number'
              ? toMoney(ticket.combinedDividend, { decimalPlaces: 2 })
              : undefined
          }
          bottomLeftComponent={<StatusSilks ticket={ticket as Ticket} />}
        />
      )}

      {!shouldHideEventDetails && (
        <BetCard.EventDetails
          heading={
            leg.raceNumber && leg.meetingName ? (
              <RacingTitle
                races={[{ raceNumber: leg.raceNumber }]}
                meetingName={leg.meetingName}
                isMultipleRaceSelection={false}
              />
            ) : (
              ticket.legs[0].eventName
            )
          }
          statusComponent={<CountdownStatus ticket={ticket} status={status} />}
          iconComponent={<IconLegacy size='1.6rem' type='races' />}
          linkInfoPrimary={linkInfo}
        />
      )}

      {!shouldHideSelectionInfo && (
        <BetCard.SelectionInfo
          heading={ticket.productName}
          subHeading={sortedLegs.map(srmLeg => {
            const { selectionDetails } = srmLeg.selections[0]
            const { name, srmPosition, number } = selectionDetails[0].acceptorDetails[0]
            const srmPositionText = sameRaceMultiPositionToText(srmPosition)
            return (
              <React.Fragment key={srmPositionText + number}>
                {srmPositionText}: {number} - {name}
                <br />
              </React.Fragment>
            )
          })}
          priceWin={ticket.combinedDividend?.toFixed(2)}
          iconComponent={<Icon size='2.4rem' name='CustomBetslip' color={colors.neutral[800]} />}
          linkInfo={linkInfo}
        />
      )}
    </>
  )
}
